/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * QuoteReview - 견적 요청 통합 리뷰 컴포넌트
 * Shared component for both quote creation (Step5Review) and quote detail page
 * Supports both form context data and API response data
 *
 * Architecture: Feature-Based with Clean Architecture
 * - Pure presentation component
 * - Data agnostic (works with form or API data)
 * - Edit functionality optional (determined by onEdit prop)
 * - Category-aware rendering
 */

import { type FC } from "react";
import { extractQuoteData } from "./utils/dataExtractor";
import {
  ItemsSection,
  ConstructionInfoSection,
  AdditionalConditionsSection,
  DescriptionSection,
  AttachmentsSection,
} from "./sections";
import { DeliveryInfoCard, BudgetInfoCard, ManagerInfoCard } from "./cards";
import type { QuoteReviewProps } from "./types";

/**
 * QuoteReview Component
 *
 * Consolidated component for displaying quote information
 * Used in both:
 * - Step5Review (quote creation final review)
 * - QuoteDetailPage (quote detail view)
 *
 * Data flow:
 * 1. Accepts data via discriminated union (form or API)
 * 2. Normalizes data internally via extractQuoteData()
 * 3. Renders appropriate sections based on category type
 * 4. Optional edit handlers shown only when provided
 */
export const QuoteReview: FC<QuoteReviewProps> = ({
  dataSource,
  onEdit,
  categoryType = "product",
  selectedAddress,
  mode = "detail",
  onMatchItem,
}) => {
  // Extract and normalize data
  const data = extractQuoteData(dataSource);
  return (
    <div className="flex flex-col gap-4 sm:gap-8">
      {/* 1. Items Section - Product only */}
      {categoryType === "product" && (
        <ItemsSection
          title="물품 항목"
          data={data}
          onEdit={onEdit?.items}
          mode={mode}
          categoryType={categoryType}
          onMatchItem={onMatchItem}
        >
          {null}
        </ItemsSection>
      )}

      {/* 3. Construction Info Section - Construction only */}
      {categoryType === "construction" && (
        <ConstructionInfoSection
          title="공사 상세 정보"
          data={data}
          onEdit={onEdit?.constructionInfo}
          mode={mode}
          categoryType={categoryType}
        >
          {null}
        </ConstructionInfoSection>
      )}

      {/* 4. Additional Conditions/Requests Section - Always shown */}
      <AdditionalConditionsSection
        title={categoryType === "product" ? "추가 조건" : "추가 요청사항"}
        data={data}
        onEdit={onEdit?.conditions}
        mode={mode}
        categoryType={categoryType}
      >
        {null}
      </AdditionalConditionsSection>

      {/* 5. Description Section - Always shown */}
      <DescriptionSection
        title="전달사항"
        data={data}
        onEdit={onEdit?.description}
        mode={mode}
        categoryType={categoryType}
      >
        {null}
      </DescriptionSection>

      {/* 6. Attachments Section - Always shown */}
      <AttachmentsSection
        title="첨부파일"
        data={data}
        onEdit={onEdit?.attachments}
        mode={mode}
        categoryType={categoryType}
        onPreviewFile={undefined} // Parent component should handle preview
      >
        {null}
      </AttachmentsSection>

      {/* 7. Three-Column Cards (DetailPage Design) - Always shown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-8">
        {/* Delivery Info Card */}
        <DeliveryInfoCard
          title="배송 정보"
          data={data}
          onEdit={onEdit?.delivery}
          mode={mode}
          selectedAddress={selectedAddress}
        />

        {/* Budget Info Card */}
        <BudgetInfoCard
          title="예산"
          data={data}
          onEdit={onEdit?.budget}
          mode={mode}
        >
          {null}
        </BudgetInfoCard>

        {/* Manager Info Card */}
        <ManagerInfoCard
          title="담당자 정보"
          data={data}
          onEdit={onEdit?.manager}
          mode={mode}
        >
          {null}
        </ManagerInfoCard>
      </div>
    </div>
  );
};
